import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  generalConfigFromGlobal,
  mergeEnvToBaseUrls,
  resolveFilterParams,
  resolveGlobalParams,
  resolveRowPayload,
} from '../../../shared/workspace-element-context';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';

@Component({
    selector: 'gtw-aggregation-trigger',
    templateUrl: './aggregation-trigger.component.html',
    styleUrls: ['./aggregation-trigger.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AggregationTriggerComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  private isInitialized = false;
  pageTitle = 'Aggregation Trigger';
  triggerOptions = [
    { name: 'AE/12/01', value: 'AE/12/01' },
    { name: 'BE/12/01', value: 'BE/12/01' },
    { name: 'CE/12/01', value: 'CE/12/01' },
  ];
  selectedValues: string[] = [];

  showConfirmDialog = false;
  confirmMode: 'selected' | 'whole' = 'selected';
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.baseURLs = mergeEnvToBaseUrls(this.baseURLs, this.gtwGlobal.GENERAL_CONFIG);
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = resolveGlobalParams(this.adminScreenContext, this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = resolveFilterParams(this.adminScreenContext, this.gtwWorkspace);
    this.rowData = resolveRowPayload(this.adminScreenContext, this.gtwWorkspace);
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.cdr.detectChanges();
  }

  triggerSelected(): void {
    this.confirmMode = 'selected';
    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      title: 'Trigger Selected Aggregation',
      message: 'Are you sure you want to trigger the aggregation for selected LEID',
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  triggerWhole(): void {
    this.confirmMode = 'whole';
    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      title: 'Trigger Whole Aggregation',
      message: 'Are you sure you want to trigger whole aggregation',
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }

  onConfirm(): void {
    this.showConfirmDialog = false;
    const selectedLabel = this.selectedValues.join(', ');
    const actionMessage =
      this.confirmMode === 'selected'
        ? `Aggregation trigger requested${selectedLabel ? ` for ${selectedLabel}` : ''}.`
        : 'Whole aggregation trigger requested.';
    this.gtwSnackbarService.openSnackBar(actionMessage, 'gtw-snackbar--green');
    this.requestGridUpdate.emit('refresh');
    this.cdr.detectChanges();
  }

  onCancelConfirm(): void {
    this.showConfirmDialog = false;
    this.cdr.detectChanges();
  }

  toggleSelection(value: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedValues.includes(value)) {
        this.selectedValues = [...this.selectedValues, value];
      }
      return;
    }
    this.selectedValues = this.selectedValues.filter((v) => v !== value);
  }
}
